import {
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import {
  FilterCriteria,
  HumanOperator,
  humanOperatorToOperator,
} from '../constants';
import { PiMinusThin, PiPlusThin } from 'react-icons/pi';
import { v4 as uuidv4 } from 'uuid';
import { ShapeSelect } from '../../Shapes/ShapeSelects/ShapeSelect';
import { checkIsBooleanField, getValueTypesByField } from '../utils';
import { useFilterContext } from '../../Map/FilterContext';
import { useLocalization } from '@ais-ui/locale-management';
import { FilterParser } from './utils/FilterParser';
import { FilterAST, Expression } from './utils/FilterAST';

// Define the type locally since it's not exported from @radix-ui/react-dismissable-layer
type PointerDownOutsideEvent = CustomEvent<{ originalEvent: Event }>;

export interface FilterBuilderProps {
  id?: string;
  value: string;
  onChange: (value: string | undefined) => void;
}

export type SelectType = Record<'value' | 'label', string>;

interface FieldSelectProps {
  selectedField: string | undefined;
  setSelectedField: (value: string | undefined) => void;
}

interface OperatorSelectProps {
  selectedField: string | undefined;
  selectedHumanOperator: HumanOperator | undefined;
  setSelectedHumanOperator: (value: HumanOperator | undefined) => void;
}

interface ValueSelectProps {
  selectedField: string | undefined;
  selectedValue: string | undefined;
  setSelectedValue: (value: string | undefined) => void;
}

interface BuildItemRecord {
  id: string;
  selectedField: string | undefined;
  selectedLogicalOperator: string | undefined;
  selectedHumanOperator: HumanOperator | undefined;
  selectedValue: string | undefined | boolean;
  astNode?: Expression; // Reference to original AST node
}

const isEventInsideDialogContent = (event: Event | undefined): boolean => {
  if (!event) return false;

  const composedPath =
    typeof event.composedPath === 'function' ? event.composedPath() : [];

  return composedPath.some((target) => {
    return (
      target instanceof HTMLElement && target.dataset?.slot === 'dialog-content'
    );
  });
};

/**
 * Converts AST to BuildItemRecord array for UI rendering
 */
function mapASTToBuildItems(
  ast: FilterAST,
  filterOptions: Record<string, FilterCriteria>,
): BuildItemRecord[] {
  if (ast.expressions.length === 0) {
    return [
      {
        id: uuidv4(),
        selectedLogicalOperator: undefined,
        selectedField: undefined,
        selectedHumanOperator: undefined,
        selectedValue: undefined,
      },
    ];
  }

  return ast.expressions.map((expr, index) => {
    const item: BuildItemRecord = {
      id: uuidv4(),
      selectedLogicalOperator: undefined,
      selectedField: undefined,
      selectedHumanOperator: undefined,
      selectedValue: undefined,
      astNode: expr,
    };

    // Set logical operator (except for first expression)
    if (index > 0 && ast.logicalOperators[index - 1]) {
      item.selectedLogicalOperator =
        ast.logicalOperators[index - 1].operator.toLowerCase();
    }

    // Extract field
    if (expr.field) {
      item.selectedField = expr.field.name;
    }

    // Extract operator and value
    if (expr.operator && expr.value) {
      const operatorSymbol = expr.operator.operator;
      const value = expr.value.value;

      item.selectedHumanOperator = determineHumanOperatorFromAST(
        operatorSymbol,
        value,
      );
      item.selectedValue = extractCleanValueFromAST(
        operatorSymbol,
        value,
        item.selectedHumanOperator,
      );
    }

    // Handle boolean fields
    if (
      item.selectedField &&
      checkIsBooleanField(
        getValueTypesByField(item.selectedField, filterOptions),
      )
    ) {
      item.selectedHumanOperator = HumanOperator.Boolean;
      item.selectedValue = true;
    }

    return item;
  });
}

/**
 * Determines the human operator from AST operator and value
 */
function determineHumanOperatorFromAST(
  operatorSymbol: string,
  value: string,
): HumanOperator {
  // Handle wildcard patterns
  if (operatorSymbol === '=' || operatorSymbol === '!=') {
    const isNegated = operatorSymbol === '!=';

    if (value.startsWith('*') && value.endsWith('*')) {
      return isNegated ? HumanOperator.NotContains : HumanOperator.Contains;
    } else if (value.startsWith('*')) {
      return isNegated ? HumanOperator.DoesNotEndWith : HumanOperator.EndsWith;
    } else if (value.endsWith('*')) {
      return isNegated
        ? HumanOperator.DoesNotStartWith
        : HumanOperator.StartsWith;
    } else {
      return isNegated ? HumanOperator.NotEqual : HumanOperator.Equal;
    }
  }

  // Map other operators directly
  const operatorMap: Record<string, HumanOperator> = {
    '>': HumanOperator.GreaterThan,
    '<': HumanOperator.LessThan,
    '>=': HumanOperator.GreaterThanOrEqual,
    '<=': HumanOperator.LessThanOrEqual,
    INSIDE_AREA: HumanOperator.InsideArea,
    OUTSIDE_AREA: HumanOperator.OutsideArea,
    INSIDE: HumanOperator.InsideCircle,
    OUTSIDE: HumanOperator.OutsideCircle,
    BOOL: HumanOperator.Boolean,
  };

  return operatorMap[operatorSymbol] || HumanOperator.Equal;
}

/**
 * Extracts clean value by removing wildcard patterns
 */
function extractCleanValueFromAST(
  operatorSymbol: string,
  value: string,
  humanOperator?: HumanOperator,
): string {
  if (!humanOperator) return value;

  switch (humanOperator) {
    case HumanOperator.Contains:
    case HumanOperator.NotContains:
      if (value.startsWith('*') && value.endsWith('*')) {
        return value.slice(1, -1);
      }
      break;
    case HumanOperator.StartsWith:
    case HumanOperator.DoesNotStartWith:
      if (value.endsWith('*')) {
        return value.slice(0, -1);
      }
      break;
    case HumanOperator.EndsWith:
    case HumanOperator.DoesNotEndWith:
      if (value.startsWith('*')) {
        return value.slice(1);
      }
      break;
  }

  return value;
}

/**
 * Generates filter definition string from build items
 */
function generateDefinitionFromBuildItems(
  items: BuildItemRecord[],
  filterOptions: Record<string, FilterCriteria>,
): string {
  let definition = '';

  for (const item of items) {
    if (
      !item.selectedField ||
      !item.selectedHumanOperator ||
      (item.selectedValue === undefined &&
        !checkIsBooleanField(
          getValueTypesByField(item.selectedField, filterOptions),
        ))
    ) {
      continue;
    }

    const types = getValueTypesByField(item.selectedField, filterOptions);

    let value;
    if (checkIsBooleanField(types)) {
      value = '';
    } else {
      // Check if field is purely numeric (only contains 'number' type)
      const isNumberField = types.length === 1 && types[0] === 'number';

      // Check if operator uses wildcards
      const isWildcardOperator = [
        HumanOperator.Contains,
        HumanOperator.NotContains,
        HumanOperator.StartsWith,
        HumanOperator.DoesNotStartWith,
        HumanOperator.EndsWith,
        HumanOperator.DoesNotEndWith,
      ].includes(item.selectedHumanOperator);

      // Apply wildcard patterns based on human operator
      if (
        item.selectedHumanOperator === HumanOperator.Contains ||
        item.selectedHumanOperator === HumanOperator.NotContains
      ) {
        value = `"*${item.selectedValue}*"`;
      } else if (
        item.selectedHumanOperator === HumanOperator.StartsWith ||
        item.selectedHumanOperator === HumanOperator.DoesNotStartWith
      ) {
        value = `"${item.selectedValue}*"`;
      } else if (
        item.selectedHumanOperator === HumanOperator.EndsWith ||
        item.selectedHumanOperator === HumanOperator.DoesNotEndWith
      ) {
        value = `"*${item.selectedValue}"`;
      } else {
        // For non-wildcard operators on pure number fields, don't add quotes
        // UNLESS it's an MMSI field with leading zeros that would be lost
        const isMmsiField =
          item.selectedField?.toUpperCase().includes('MMSI') ?? false;
        const hasLeadingZero =
          typeof item.selectedValue === 'string' &&
          item.selectedValue.startsWith('0') &&
          item.selectedValue.length > 1;

        if (isNumberField && !isWildcardOperator && !(isMmsiField && hasLeadingZero)) {
          value = item.selectedValue;
        } else {
          value = `"${item.selectedValue}"`;
        }
      }
    }

    const operatorText = humanOperatorToOperator(item.selectedHumanOperator);

    if (item.selectedLogicalOperator) {
      if (checkIsBooleanField(types)) {
        definition += `${item.selectedLogicalOperator} ${item.selectedField} `;
      } else {
        definition += `${item.selectedLogicalOperator} ${item.selectedField} ${operatorText} ${value} `;
      }
    } else {
      if (checkIsBooleanField(types)) {
        definition += `${item.selectedField} `;
      } else {
        definition += `${item.selectedField} ${operatorText} ${value} `;
      }
    }
  }

  return definition.trim();
}

const FieldSelect = ({ selectedField, setSelectedField }: FieldSelectProps) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const availableFieldSelectables: SelectType[] = useMemo(() => {
    return Object.keys(filterOptions)
      .filter((filterName) =>
        filterName.toLowerCase().includes(search.toLowerCase()),
      )
      .map((filterName) => ({
        label: filterName.toLowerCase(),
        value: filterName.toLowerCase(),
      }));
  }, [filterOptions, search]);

  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 32;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;
    const MIN_HEIGHT = 60;

    let contentHeight = COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS;

    if (!availableFieldSelectables.length) {
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      contentHeight += availableFieldSelectables.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [availableFieldSelectables.length]);

  const handlePointerDownOutside = useCallback(
    (event: PointerDownOutsideEvent) => {
      const originalEvent = event.detail?.originalEvent;

      if (isEventInsideDialogContent(originalEvent)) {
        event.preventDefault();
        if (originalEvent && 'stopPropagation' in originalEvent) {
          (
            originalEvent as unknown as { stopPropagation: () => void }
          ).stopPropagation();
        }
        if (originalEvent && 'preventDefault' in originalEvent) {
          (
            originalEvent as unknown as { preventDefault: () => void }
          ).preventDefault();
        }
        setOpen(false);
      }
    },
    [setOpen],
  );

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        <Button
          id="filter-field"
          variant="outline"
          role="combobox"
          onClick={() => setOpen(!open)}
          className="w-full min-w-[120px] justify-between pr-3"
        >
          <div className="flex items-center justify-between w-full">
            <span className="truncate mr-2">
              {selectedField ? (
                availableFieldSelectables?.find(
                  (field) => field.value === selectedField,
                )?.label
              ) : (
                <span className="text-muted-foreground">Select a field</span>
              )}
            </span>
            <ChevronsUpDown className="size-4 shrink-0 opacity-50 ml-1" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal
        align="start"
        className="w-full p-0"
        style={{ height: `${calculatedHeight}px` }}
        onPointerDownOutside={handlePointerDownOutside}
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={messages.filtersBuilderFilterFieldPlaceholder}
            onValueChange={(value) => {
              setSearch(value);
            }}
            value={search}
          />
          <CommandEmpty>{messages.filterBuilderNoFieldsMatch}</CommandEmpty>
          <ScrollArea className="grow p-0 overflow-y-auto border rounded-lg">
            {availableFieldSelectables.map((fieldSelect) => (
              <CommandItem
                key={fieldSelect.value}
                value={fieldSelect.value}
                onSelect={(currentValue) => {
                  setSelectedField(
                    currentValue === selectedField ? undefined : currentValue,
                  );
                  setOpen(false);
                }}
              >
                <div className="flex justify-between w-full">
                  {fieldSelect.label}
                  <Check
                    className={cn(
                      'mr-1 size-4',
                      selectedField === fieldSelect.value
                        ? 'opacity-100'
                        : 'opacity-0',
                    )}
                  />
                </div>
              </CommandItem>
            ))}
          </ScrollArea>
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
};

function getHumanOperatorsByField(
  field: string | undefined,
  filterOptions: Record<string, FilterCriteria>,
): HumanOperator[] {
  if (!field) {
    return [];
  }

  const uniqueHumanOperators = new Set<HumanOperator>();
  const uppercasedSelectedField = field.toUpperCase();

  if (
    Object.prototype.hasOwnProperty.call(filterOptions, uppercasedSelectedField)
  ) {
    filterOptions[uppercasedSelectedField].operators.forEach(
      (humanOperator) => {
        uniqueHumanOperators.add(humanOperator);
      },
    );
  }

  return Array.from(new Set(uniqueHumanOperators));
}

const OperatorSelect = ({
  selectedField,
  selectedHumanOperator,
  setSelectedHumanOperator,
}: OperatorSelectProps) => {
  const [open, setOpen] = useState(false);
  const { filterOptions } = useFilterContext();
  const { messages } = useLocalization();

  const availableOperatorSelectables = useMemo(() => {
    const humanOperators = getHumanOperatorsByField(
      selectedField,
      filterOptions,
    );
    return humanOperators;
  }, [filterOptions, selectedField]);

  const calculatedOperatorHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 30;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;
    const MIN_HEIGHT = 60;

    let contentHeight = PADDING_AND_BORDERS;

    if (!availableOperatorSelectables.length) {
      contentHeight += 50;
    } else {
      contentHeight +=
        availableOperatorSelectables.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [availableOperatorSelectables.length]);

  const isBooleanField = useMemo(() => {
    return checkIsBooleanField(
      getValueTypesByField(selectedField, filterOptions),
    );
  }, [filterOptions, selectedField]);

  const handlePointerDownOutside = useCallback(
    (event: PointerDownOutsideEvent) => {
      const originalEvent = event.detail?.originalEvent;

      if (isEventInsideDialogContent(originalEvent)) {
        event.preventDefault();
        if (originalEvent && 'stopPropagation' in originalEvent) {
          (
            originalEvent as unknown as { stopPropagation: () => void }
          ).stopPropagation();
        }
        if (originalEvent && 'preventDefault' in originalEvent) {
          (
            originalEvent as unknown as { preventDefault: () => void }
          ).preventDefault();
        }
        setOpen(false);
      }
    },
    [setOpen],
  );

  if (isBooleanField) {
    return (
      <div className="flex w-full h-full items-center text-xs text-muted-foreground">
        <p>{messages.FiltersBooleanFieldHelpText}</p>
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              id="filter-operator"
              variant="outline"
              role="combobox"
              disabled={isBooleanField}
              onClick={() => setOpen(!open)}
              className={cn(
                'w-full min-w-[150px] justify-between pr-3',
                !selectedField &&
                  'opacity-50 cursor-default hover:bg-background',
                isBooleanField && 'invisible',
              )}
            >
              {selectedHumanOperator ? (
                availableOperatorSelectables?.find(
                  (field) => field === selectedHumanOperator,
                )
              ) : (
                <span>{messages.filtersBuilderFilterOperatorPlaceholder}</span>
              )}
              <span className="flex-1"></span>
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {messages.filtersBuilderFilterOperatorTooltipContent}
        </TooltipContent>
      </Tooltip>
      {selectedField && (
        <PopoverContentNoPortal
          align="start"
          className="w-full p-0"
          style={{ height: `${calculatedOperatorHeight}px` }}
          onPointerDownOutside={handlePointerDownOutside}
        >
          <Command shouldFilter={false}>
            <ScrollArea className="grow p-0 overflow-y-auto border rounded-lg">
              {availableOperatorSelectables.map((humanOperator) => (
                <CommandItem
                  key={humanOperator.toString()}
                  value={humanOperator.toString()}
                  onSelect={(currentValue) => {
                    setSelectedHumanOperator(
                      currentValue === selectedHumanOperator?.toString()
                        ? undefined
                        : humanOperator,
                    );
                    setOpen(false);
                  }}
                >
                  <div className="flex justify-between w-full">
                    {humanOperator.toString()}
                    <Check
                      className={cn(
                        'ml-4 mr-1 size-4',
                        selectedHumanOperator === humanOperator
                          ? 'opacity-100'
                          : 'opacity-0',
                      )}
                    />
                  </div>
                </CommandItem>
              ))}
            </ScrollArea>
          </Command>
        </PopoverContentNoPortal>
      )}
    </Popover>
  );
};

function getValueSuggestionsByField(
  field: string | undefined,
  filterOptions: Record<string, FilterCriteria>,
): string[] {
  if (!field) {
    return [];
  }

  const valueSuggestions: string[] = [];
  const uppercasedField = field.toUpperCase();

  if (Object.prototype.hasOwnProperty.call(filterOptions, uppercasedField)) {
    filterOptions[uppercasedField].values.forEach((suggestionValue) => {
      valueSuggestions.push(suggestionValue);
    });
  }

  return valueSuggestions;
}

const ValueSelect = ({
  selectedField,
  selectedValue,
  setSelectedValue,
}: ValueSelectProps) => {
  const { messages } = useLocalization();
  const { filterOptions } = useFilterContext();
  const [open, setOpen] = useState(false);

  const isBooleanField = useMemo(() => {
    return checkIsBooleanField(
      getValueTypesByField(selectedField, filterOptions),
    );
  }, [filterOptions, selectedField]);

  const availableValueSelectables = useMemo(() => {
    const values = getValueSuggestionsByField(selectedField, filterOptions);
    return values;
  }, [filterOptions, selectedField]);

  const filteredSuggestions = useMemo(() => {
    return availableValueSelectables.filter((value) =>
      value
        .toLowerCase()
        .includes(selectedValue ? selectedValue.toLowerCase() : ''),
    );
  }, [availableValueSelectables, selectedValue]);

  const calculatedValueHeight = useMemo(() => {
    const COMMAND_ITEM_HEIGHT = 30;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 200;
    const MIN_HEIGHT = 60;

    let contentHeight = PADDING_AND_BORDERS;

    if (!filteredSuggestions.length) {
      contentHeight += 50;
    } else {
      contentHeight += filteredSuggestions.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(Math.max(contentHeight, MIN_HEIGHT), MAX_HEIGHT);
  }, [filteredSuggestions.length]);

  if (isBooleanField) {
    return null;
  }

  if (selectedField?.toLowerCase() === 'position') {
    return (
      <div className="flex-1 min-w-0 overflow-hidden">
        <ShapeSelect
          value={selectedValue}
          onChange={setSelectedValue}
          allowedGeometryTypes={['Polygon']}
        />
      </div>
    );
  }

  if (selectedField?.toLowerCase() === 'circle_position') {
    return (
      <div className="flex-1 min-w-0 overflow-hidden">
        <ShapeSelect
          value={selectedValue}
          onChange={setSelectedValue}
          allowedGeometryTypes={['Circle']}
        />
      </div>
    );
  }

  const dataTypes = selectedField
    ? filterOptions[selectedField.toUpperCase()]?.data_types
    : undefined;
  const inputType = dataTypes?.includes('number') ? 'number' : 'text';

  return (
    <Command
      className={cn(
        'overflow-visible bg-transparent flex-1 min-w-0',
        isBooleanField && 'invisible',
      )}
      shouldFilter={false}
    >
      <div className="relative">
        <div className="group border border-input px-3 py-2 text-sm rounded-md">
          <div className="flex gap-1 flex-wrap">
            <input
              id="filter-value"
              type={inputType}
              disabled={isBooleanField}
              value={selectedValue || ''}
              onChange={(e) => {
                setSelectedValue(e.target.value);
              }}
              onFocus={() => {
                setOpen(true);
              }}
              onBlur={(e) => {
                const currentTarget = e.currentTarget;
                setTimeout(() => {
                  if (
                    currentTarget &&
                    !currentTarget.contains(document.activeElement)
                  ) {
                    setOpen(false);
                  }
                }, 150);
              }}
              placeholder={messages.filtersBuilderFilterValuePlaceholder}
              className={cn(
                'flex w-full rounded-md bg-transparent text-sm outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 ml-2',
              )}
              autoComplete="off"
            />
          </div>
        </div>

        {selectedField && filteredSuggestions.length > 0 && open && (
          <div className="relative">
            <div
              className="absolute w-full z-10 top-0 rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in"
              style={{ height: `${calculatedValueHeight}px` }}
            >
              <ScrollArea className="grow p-0 overflow-y-auto border rounded-lg h-full">
                {filteredSuggestions.map((valueSelect) => (
                  <CommandItem
                    key={valueSelect}
                    value={valueSelect}
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onSelect={(currentValue) => {
                      setSelectedValue(valueSelect);
                      setOpen(false);
                    }}
                  >
                    <div className="flex justify-between w-full">
                      {valueSelect}
                      <Check
                        className={cn(
                          'mr-1 size-4',
                          selectedValue === valueSelect
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                    </div>
                  </CommandItem>
                ))}
              </ScrollArea>
            </div>
          </div>
        )}
      </div>
    </Command>
  );
};

interface BuildItemProps {
  isFirst: boolean;
  item: BuildItemRecord;
  onLogicalOperatorChange: (id: string, value: string | undefined) => void;
  onFieldChange: (id: string, value: string | undefined) => void;
  onOperatorChange: (id: string, value: HumanOperator | undefined) => void;
  onValueChange: (id: string, value: string | undefined) => void;
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
}

const BuildItem = ({
  isFirst,
  item,
  onLogicalOperatorChange,
  onFieldChange,
  onOperatorChange,
  onValueChange,
  onAddItem,
  onRemoveItem,
}: BuildItemProps) => {
  return (
    <div>
      {isFirst ? null : (
        <Select
          value={item.selectedLogicalOperator}
          onValueChange={(value) => {
            onLogicalOperatorChange(item.id, value);
          }}
        >
          <SelectTrigger className="w-20 font-medium mt-2">
            {item.selectedLogicalOperator}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="and">and</SelectItem>
            <SelectItem value="or">or</SelectItem>
          </SelectContent>
        </Select>
      )}
      <div
        className={cn(
          'flex items-center gap-4 rounded-lg border border-border/50 bg-muted/30 p-4 shadow-sm transition-all duration-200 hover:border-border hover:shadow-md',
          !isFirst && 'mt-3'
        )}
      >
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex w-full items-center gap-3">
            <div className="flex-1">
              <FieldSelect
                selectedField={item.selectedField}
                setSelectedField={(value) => {
                  onFieldChange(item.id, value);
                }}
              />
            </div>
            <div className="flex-1">
              <OperatorSelect
                selectedField={item.selectedField}
                selectedHumanOperator={item.selectedHumanOperator}
                setSelectedHumanOperator={(value) => {
                  onOperatorChange(item.id, value);
                }}
              />
            </div>
          </div>
          <ValueSelect
            selectedField={item.selectedField}
            selectedValue={
              typeof item.selectedValue === 'boolean' ? '' : item.selectedValue
            }
            setSelectedValue={(value) => {
              onValueChange(item.id, value);
            }}
          />
        </div>
        <div className="flex flex-col items-center gap-2">
          <Button
            type="button"
            size="icon"
            disabled={isFirst}
            variant="outline"
            className="h-8 w-8 shrink-0 transition-colors hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 disabled:opacity-40"
            onClick={() => {
              onRemoveItem(item.id);
            }}
          >
            <PiMinusThin className="size-4" />
          </Button>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="h-8 w-8 shrink-0 transition-colors hover:bg-primary/10 hover:text-primary hover:border-primary/50"
            onClick={() => {
              onAddItem();
            }}
          >
            <PiPlusThin className="size-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export const FilterBuilder = ({ id, value, onChange }: FilterBuilderProps) => {
  const { filterOptions } = useFilterContext();

  // Create parser instance
  const parser = useMemo(() => {
    return new FilterParser(filterOptions);
  }, [filterOptions]);

  // Track if changes are coming from internal updates
  const isInternalUpdate = useRef(false);
  const lastGeneratedValue = useRef(value);

  const [items, setItems] = useState<BuildItemRecord[]>(() => {
    const parseResult = parser.parse(value);
    return mapASTToBuildItems(parseResult.ast, filterOptions);
  });

  // Update items when value changes from external source
  useEffect(() => {
    // Skip if this change was generated internally
    if (isInternalUpdate.current || value === lastGeneratedValue.current) {
      isInternalUpdate.current = false;
      return;
    }

    if (value) {
      const parseResult = parser.parse(value);
      const newItems = mapASTToBuildItems(parseResult.ast, filterOptions);
      setItems(newItems);
    }
  }, [value, filterOptions, parser]);

  const generateDefinition = useCallback(
    (updatedItems: BuildItemRecord[]) => {
      const newDefinition = generateDefinitionFromBuildItems(
        updatedItems,
        filterOptions,
      );

      // Mark as internal update and track the generated value
      if (newDefinition !== lastGeneratedValue.current) {
        isInternalUpdate.current = true;
        lastGeneratedValue.current = newDefinition;
        onChange(newDefinition);
      }
    },
    [filterOptions, onChange],
  );

  const handleLogicOperatorChange = useCallback(
    (id: string, newValue: string | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id
            ? { ...item, selectedLogicalOperator: newValue }
            : item.selectedLogicalOperator !== undefined &&
                item.selectedLogicalOperator !== null
              ? { ...item, selectedLogicalOperator: newValue }
              : item,
        );
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition],
  );

  const handleFieldChange = useCallback(
    (id: string, newValue: string | undefined) => {
      const types = getValueTypesByField(newValue, filterOptions);
      if (checkIsBooleanField(types)) {
        setItems((prevItems) => {
          const newItems = prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  selectedField: newValue,
                  selectedHumanOperator: HumanOperator.Boolean,
                  selectedValue: true,
                }
              : item,
          );
          queueMicrotask(() => generateDefinition(newItems));
          return newItems;
        });
      } else {
        setItems((prevItems) => {
          const newItems = prevItems.map((item) =>
            item.id === id
              ? {
                  ...item,
                  selectedField: newValue,
                  selectedHumanOperator: HumanOperator.Equal,
                  selectedValue: '',
                }
              : item,
          );
          queueMicrotask(() => generateDefinition(newItems));
          return newItems;
        });
      }
    },
    [generateDefinition, filterOptions],
  );

  const handleOperatorChange = useCallback(
    (id: string, newValue: HumanOperator | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id ? { ...item, selectedHumanOperator: newValue } : item,
        );
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition],
  );

  const handleValueChange = useCallback(
    (id: string, newValue: string | undefined) => {
      setItems((prevItems) => {
        const newItems = prevItems.map((item) =>
          item.id === id ? { ...item, selectedValue: newValue } : item,
        );
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition],
  );

  const addItem = useCallback(() => {
    setItems((prevItems) => [
      ...prevItems,
      {
        id: uuidv4(),
        selectedLogicalOperator:
          prevItems.length > 1 && prevItems[1].selectedLogicalOperator
            ? prevItems[1].selectedLogicalOperator
            : 'and',
        selectedField: undefined,
        selectedHumanOperator: undefined,
        selectedValue: undefined,
      },
    ]);
  }, []);

  const removeItem = useCallback(
    (id: string) => {
      setItems((prevItems) => {
        const newItems = prevItems.filter((item) => item.id !== id);
        queueMicrotask(() => generateDefinition(newItems));
        return newItems;
      });
    },
    [generateDefinition],
  );

  return (
    <div id={id}>
      {items.map((item, index) => (
        <BuildItem
          key={item.id}
          isFirst={index === 0}
          item={item}
          onLogicalOperatorChange={handleLogicOperatorChange}
          onFieldChange={handleFieldChange}
          onOperatorChange={handleOperatorChange}
          onValueChange={handleValueChange}
          onRemoveItem={removeItem}
          onAddItem={addItem}
        />
      ))}
    </div>
  );
};
